
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { NutritionSearch } from "./NutritionSearch";
import { FileDown, Pencil, Trash } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { generateNutritionPDF } from "./NutritionPDF";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

export const NutritionList = () => {
  const navigate = useNavigate();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("nutrition");
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [recommendationToDelete, setRecommendationToDelete] = useState<string | null>(null);

  const { data: recommendations, isLoading } = useQuery({
    queryKey: ["nutrition-recommendations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nutrition_recommendations")
        .select(`
          *,
          students (
            first_name,
            last_name
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("nutrition_recommendations")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success(t("notifications.deleted"));
      setRecommendationToDelete(null);
    } catch (error: any) {
      console.error("Error deleting recommendation:", error);
      toast.error(error.message || "Erro ao apagar recomendação");
    }
  };

  const filteredRecommendations = recommendations?.filter((rec) => {
    const searchString = searchTerm.toLowerCase();
    return (
      rec.students.first_name.toLowerCase().includes(searchString) ||
      rec.students.last_name.toLowerCase().includes(searchString)
    );
  });

  if (isLoading) {
    return <div className="text-foreground dark:text-gray-200">{language === 'pt' ? 'A carregar recomendações...' : 'Loading recommendations...'}</div>;
  }

  const dateLocale = language === 'pt' ? ptBR : enUS;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight gradient-text text-slate-950 dark:text-white">{t("title")}</h2>
        <p className="text-muted-foreground dark:text-gray-300">
          {t("subtitle")}
        </p>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium dark:text-gray-200">{t("title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <NutritionSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
        <Button 
          onClick={() => navigate(getPath("/dashboard/nutrition/new"))}
          className="bg-flame text-white hover:bg-flame/90 rounded-xl shadow-sm hover:shadow-md transition-all"
        >
          {t("form.newRecommendation")}
        </Button>
      </div>

      <div className="border rounded-lg overflow-x-auto dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="dark:bg-gray-800/50 dark:hover:bg-gray-700/50">
              <TableHead className="dark:text-gray-300">{t("table.student")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.activityLevel")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.calories")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.protein")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.carbs")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.fats")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.date")}</TableHead>
              <TableHead className="text-right dark:text-gray-300">{t("table.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecommendations?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-foreground dark:text-gray-300">
                  {t("table.noRecommendations")}
                </TableCell>
              </TableRow>
            ) : (
              filteredRecommendations?.map((rec) => (
                <TableRow key={rec.id} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/50">
                  <TableCell className="text-foreground dark:text-gray-200">{rec.students.first_name} {rec.students.last_name}</TableCell>
                  <TableCell className="capitalize text-foreground dark:text-gray-200">{rec.activity_level.replace('_', ' ')}</TableCell>
                  <TableCell className="text-foreground dark:text-gray-200">{rec.calories} kcal</TableCell>
                  <TableCell className="text-foreground dark:text-gray-200">{rec.protein}g</TableCell>
                  <TableCell className="text-foreground dark:text-gray-200">{rec.carbs}g</TableCell>
                  <TableCell className="text-foreground dark:text-gray-200">{rec.fats}g</TableCell>
                  <TableCell className="text-foreground dark:text-gray-200">{formatDistanceToNow(new Date(rec.created_at), { 
                    addSuffix: true,
                    locale: dateLocale 
                  })}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(getPath(`/dashboard/nutrition/edit/${rec.id}`))}
                      className="hover:bg-flame/10 hover:text-flame dark:text-gray-200 dark:hover:text-flame"
                    >
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">Editar</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => generateNutritionPDF(rec)}
                      className="hover:bg-flame/10 hover:text-flame dark:text-gray-200 dark:hover:text-flame"
                    >
                      <FileDown className="h-4 w-4" />
                      <span className="sr-only">Exportar PDF</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRecommendationToDelete(rec.id)}
                      className="hover:bg-destructive/10 hover:text-destructive dark:text-gray-200 dark:hover:text-destructive"
                    >
                      <Trash className="h-4 w-4" />
                      <span className="sr-only">Apagar</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!recommendationToDelete} onOpenChange={() => setRecommendationToDelete(null)}>
        <AlertDialogContent className="bg-white dark:bg-gray-800 text-foreground dark:text-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground dark:text-white">{t("deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground dark:text-gray-400">
              {t("deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600">{t("deleteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => recommendationToDelete && handleDelete(recommendationToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
